// Torre de control del piso de cocina: batches ordenados por hora límite (LST),
// carga de equipos, alertas y avance de estado. Catering → Piso de cocina.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle, Ban, ChevronRight, Crown, Factory, Loader2, Play, Radar,
  RotateCcw, Thermometer,
} from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { PanelIntegrado } from '@/components/ui/PanelIntegrado';
import { fieldCls, num } from '@/features/inventario/shared';
import {
  CargaEquipo, ESTADO_GLOBAL_CLS, ESTADO_GLOBAL_LABEL, Estacion, OpResumen,
  PISO_ESTADO_LABEL, PisoBatch, PisoTablero, SEMAFORO_CLS, SEMAFORO_TXT,
  MOTIVOS_DETENCION, cat, catRpc, hhmm, holguraTxt, invalidateCatering, siguienteEstado,
} from './shared';

const hoyLocal = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function PisoCatering() {
  const qc = useQueryClient();
  const [fecha, setFecha] = useState(hoyLocal());
  const [estacion, setEstacion] = useState('');
  const [accion, setAccion] = useState<{ batch: PisoBatch; tipo: 'avanzar' | 'detener' } | null>(null);
  const [temp, setTemp] = useState('');
  const [porciones, setPorciones] = useState('');
  const [motivo, setMotivo] = useState(MOTIVOS_DETENCION[0]);
  const [detalle, setDetalle] = useState('');
  const [responsable, setResponsable] = useState('');
  const invalidar = () => invalidateCatering(qc);

  const { data: t, isFetching } = useQuery<PisoTablero>({
    queryKey: ['cat-piso', fecha, estacion],
    queryFn: () => catRpc<PisoTablero>('piso_tablero', { _fecha: fecha, _estacion: estacion || null }),
    refetchInterval: 20000, staleTime: 0, refetchOnMount: 'always',
  });
  const { data: carga = [] } = useQuery<CargaEquipo[]>({
    queryKey: ['cat-piso-carga', fecha],
    queryFn: () => catRpc<CargaEquipo[]>('piso_carga_equipos', { _fecha: fecha }),
  });
  const { data: estaciones = [] } = useQuery<Estacion[]>({
    queryKey: ['cat-estaciones'],
    queryFn: () => cat.list<Estacion>('estaciones', '?select=*&deleted_at=is.null&order=orden'),
  });
  const { data: ordenes = [] } = useQuery<OpResumen[]>({
    queryKey: ['cat-ops'],
    queryFn: () => catRpc<OpResumen[]>('op_listar', { _estado: null }),
  });
  const ordenDia = ordenes.find((o) => o.fecha === fecha && o.estado !== 'anulada');

  const generar = useMutation({
    mutationFn: () => catRpc<{ batches_creados: number }>('piso_generar', { _orden_id: ordenDia!.id }),
    onSuccess: (r) => { toast.success(`${r.batches_creados} batches generados en el piso`); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const demo = useMutation({
    mutationFn: () => catRpc<{ batches: number }>('piso_demo_reset', { _fecha: fecha }),
    onSuccess: (r) => { toast.success(`Escenario de demostración: ${r.batches} batches`); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const mover = useMutation({
    mutationFn: (p: Record<string, unknown>) => catRpc('batch_estado', p),
    onSuccess: () => {
      toast.success('Batch actualizado');
      setAccion(null); setTemp(''); setPorciones(''); setDetalle('');
      invalidar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const secuenciaDe = (b: PisoBatch): string[] => {
    const e = estaciones.find((x) => x.codigo === b.estacion_codigo);
    return e?.estados?.length ? e.estados : undefined as unknown as string[];
  };
  const proximo = (b: PisoBatch) => siguienteEstado(b.estado, secuenciaDe(b));

  const abrirAvanzar = (b: PisoBatch) => {
    const sig = proximo(b);
    if (!sig) { toast.error('El batch ya está en su último estado'); return; }
    // Punto crítico: pasar a "listo" exige temperatura si la receta la define
    if (sig === 'listo' && b.temp_min_c != null) {
      setAccion({ batch: b, tipo: 'avanzar' }); setTemp(''); setResponsable(b.responsable ?? '');
      return;
    }
    if (sig === 'porcionado' || sig === 'despachado') {
      setAccion({ batch: b, tipo: 'avanzar' }); setPorciones(String(Math.round(Number(b.porciones_reales ?? b.porciones_plan))));
      setResponsable(b.responsable ?? ''); return;
    }
    mover.mutate({ _batch_id: b.id, _estado: sig, _responsable: b.responsable ?? null });
  };

  const r = t?.resumen;
  const pendientes = useMemo(() => (t?.batches ?? []).filter((b) => b.estado !== 'despachado'), [t]);
  const despachados = useMemo(() => (t?.batches ?? []).filter((b) => b.estado === 'despachado'), [t]);

  // Ventana del Gantt: de la primera a la última barra del día
  const ventana = useMemo(() => {
    const todos = carga.flatMap((c) => c.barras);
    if (!todos.length) return null;
    const min = Math.min(...todos.map((b) => new Date(b.inicio).getTime()));
    const max = Math.max(...todos.map((b) => new Date(b.fin).getTime()));
    return { min, max, span: Math.max(max - min, 1) };
  }, [carga]);

  return (
    <div className="p-4 space-y-4">
      {/* Cabecera de estado global */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-lg font-semibold flex items-center gap-2">
          <Radar className="w-5 h-5" /> Torre de control del piso
          {isFetching && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        </h1>
        <input type="date" className={`${fieldCls} !w-auto`} value={fecha} onChange={(e) => setFecha(e.target.value)} />
        <select className={`${fieldCls} !w-auto`} value={estacion} onChange={(e) => setEstacion(e.target.value)}>
          <option value="">Todas las estaciones</option>
          {estaciones.map((e) => <option key={e.id} value={e.codigo}>{e.nombre}</option>)}
        </select>
        {ordenDia && (
          <Button size="sm" variant="outline" disabled={generar.isPending} onClick={() => generar.mutate()}>
            <Factory className="mr-1 h-4 w-4" /> Generar batches de {ordenDia.codigo}
          </Button>
        )}
        <Button size="sm" variant="ghost" disabled={demo.isPending} onClick={() => demo.mutate()}>
          <RotateCcw className="mr-1 h-4 w-4" /> Escenario demo
        </Button>
      </div>

      {r && (
        <div className={`rounded-lg px-4 py-3 flex flex-wrap items-center justify-between gap-4 ${ESTADO_GLOBAL_CLS[r.estado_global]}`}>
          <div>
            <div className="text-2xl font-black">{ESTADO_GLOBAL_LABEL[r.estado_global]}</div>
            <div className="text-xs opacity-90">
              {r.despachados}/{r.total_batches} batches despachados · {num(r.porciones_despachadas, 0)}/{num(r.porciones_plan, 0)} porciones
            </div>
          </div>
          <div className="text-center">
            <div className="text-3xl font-black font-mono tabular-nums">
              {r.minutos_al_servicio == null ? '—'
                : r.minutos_al_servicio < 0 ? holguraTxt(r.minutos_al_servicio)
                : r.minutos_al_servicio >= 60 ? `T− ${Math.floor(r.minutos_al_servicio / 60)}h ${String(r.minutos_al_servicio % 60).padStart(2, '0')}m`
                : `T− ${r.minutos_al_servicio} min`}
            </div>
            <div className="text-xs opacity-90">próximo servicio {hhmm(r.proximo_servicio)}</div>
          </div>
          <div className="flex gap-4 text-center text-sm">
            <div><div className="text-xl font-bold font-mono">{r.en_curso}</div><div className="text-xs opacity-80">en cocción</div></div>
            <div><div className="text-xl font-bold font-mono">{r.pendientes}</div><div className="text-xs opacity-80">pendientes</div></div>
            <div><div className="text-xl font-bold font-mono">{r.en_rojo}</div><div className="text-xs opacity-80">en rojo</div></div>
            <div><div className="text-xl font-bold font-mono">{r.detenidos}</div><div className="text-xs opacity-80">detenidos</div></div>
          </div>
        </div>
      )}

      {/* Alertas priorizadas */}
      {!!t?.alertas.length && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" /> Alertas ({t.alertas.length})
          </CardTitle></CardHeader>
          <CardContent className="space-y-1">
            {t.alertas.map((a) => (
              <div key={a.batch_id + a.tipo} className={`text-sm rounded-md px-3 py-1.5 ${
                a.tipo === 'sanitario' ? 'bg-sky-100 text-sky-900 dark:bg-sky-900/30 dark:text-sky-200'
                : a.tipo === 'detenido' ? 'bg-purple-100 text-purple-900 dark:bg-purple-900/30 dark:text-purple-200'
                : 'bg-red-100 text-red-900 dark:bg-red-900/30 dark:text-red-200'}`}>
                <span className="font-mono text-xs mr-2">{a.codigo}</span>{a.texto}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {t?.cuello && (
        <div className="rounded-md border border-amber-400 bg-amber-50 px-3 py-2 text-sm dark:bg-amber-900/20">
          <b>Cuello de botella:</b> {t.cuello.equipo} — {t.cuello.demanda} batches compiten por {t.cuello.unidades} unidad(es).
          Mueva un batch de hora o pártalo.
        </div>
      )}

      {/* Lista priorizada por holgura (lo de arriba es lo que hay que empezar YA) */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Batches por hora límite ({pendientes.length} en curso)</CardTitle>
          <p className="text-xs text-muted-foreground">
            Ordenados por holgura: la primera fila es lo que hay que empezar ahora. La hora límite (LST) es
            servicio − duración − porcionado − despacho.
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-2">N°</th><th className="py-2 pr-2">Batch</th><th className="py-2 pr-2">Receta</th>
              <th className="py-2 pr-2">Estación / equipo</th><th className="py-2 pr-2 text-right">Porciones</th>
              <th className="py-2 pr-2">Servicio</th><th className="py-2 pr-2">Empezar antes de</th>
              <th className="py-2 pr-2 text-right">Holgura</th><th className="py-2 pr-2">Estado</th>
              <th className="py-2 pr-2">Responsable</th><th className="py-2 pr-2">Sanidad</th><th />
            </tr></thead>
            <tbody>
              {pendientes.map((b, i) => (
                <tr key={b.id} className={`border-b last:border-0 ${b.semaforo === 'rojo' ? 'bg-red-50 dark:bg-red-900/10'
                  : b.semaforo === 'ambar' ? 'bg-amber-50 dark:bg-amber-900/10'
                  : b.semaforo === 'detenido' ? 'bg-purple-50 dark:bg-purple-900/10'
                  : b.semaforo === 'sanitario' ? 'bg-sky-50 dark:bg-sky-900/10' : ''}`}>
                  <td className="py-1.5 pr-2 text-muted-foreground">{i + 1}</td>
                  <td className="py-1.5 pr-2 font-mono text-xs">
                    {b.codigo}{b.sub_total > 1 && <span className="text-muted-foreground"> ({b.sub_indice}/{b.sub_total})</span>}
                  </td>
                  <td className="py-1.5 pr-2">
                    {b.es_critico && <Crown className="inline w-3.5 h-3.5 text-yellow-500 mr-1" />}
                    {b.receta}
                    {b.es_dieta && <span className="ml-1 rounded-full bg-fuchsia-600 px-1.5 py-0.5 text-[10px] font-bold text-white">DIETA</span>}
                    {(b.alergenos ?? []).map((a) => (
                      <span key={a} className="ml-1 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{a}</span>
                    ))}
                  </td>
                  <td className="py-1.5 pr-2 text-xs">{b.estacion}{b.equipo && <span className="text-muted-foreground"> · {b.equipo}</span>}</td>
                  <td className="py-1.5 pr-2 text-right font-mono tabular-nums">{num(b.porciones_plan, 0)}</td>
                  <td className="py-1.5 pr-2 font-mono">{hhmm(b.hora_servicio)}</td>
                  <td className="py-1.5 pr-2 font-mono">{b.estado === 'planificado' ? hhmm(b.latest_start) : `fin ${hhmm(b.fin_proyectado)}`}</td>
                  <td className={`py-1.5 pr-2 text-right font-mono font-semibold ${SEMAFORO_TXT[b.semaforo]}`}>{holguraTxt(b.holgura_min)}</td>
                  <td className="py-1.5 pr-2">
                    <span className={`rounded-full border-2 px-2 py-0.5 text-xs font-medium ${SEMAFORO_CLS[b.semaforo]}`}>
                      {PISO_ESTADO_LABEL[b.estado] ?? b.estado}
                    </span>
                    {b.motivo && <div className="text-xs text-purple-700 dark:text-purple-400">⛔ {b.motivo}</div>}
                  </td>
                  <td className="py-1.5 pr-2 text-xs">{b.responsable ?? '—'}</td>
                  <td className="py-1.5 pr-2 text-xs">
                    {b.temperatura_c != null && (
                      <span className="inline-flex items-center gap-0.5"><Thermometer className="w-3 h-3" />{Number(b.temperatura_c).toFixed(0)}°</span>
                    )}
                    {b.peligro_min != null && (
                      <span className={`ml-1 font-semibold ${b.peligro_semaforo === 'vencido' ? 'text-red-600'
                        : b.peligro_semaforo === 'alarma' ? 'text-amber-600' : 'text-muted-foreground'}`}>
                        {Math.round(b.peligro_min)}′
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 text-right whitespace-nowrap">
                    {b.estado === 'detenido' ? (
                      <Button size="sm" variant="outline"
                        onClick={() => mover.mutate({ _batch_id: b.id, _estado: 'reanudar' })}>
                        <Play className="w-3.5 h-3.5" /> Reanudar
                      </Button>
                    ) : (
                      <>
                        <Button size="sm" variant="outline" onClick={() => abrirAvanzar(b)}>
                          {PISO_ESTADO_LABEL[proximo(b) ?? ''] ?? 'Avanzar'} <ChevronRight className="w-3.5 h-3.5" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setAccion({ batch: b, tipo: 'detener' }); setMotivo(MOTIVOS_DETENCION[0]); setDetalle(''); }}>
                          <Ban className="w-3.5 h-3.5 text-purple-600" />
                        </Button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {!pendientes.length && (
                <tr><td colSpan={12} className="py-8 text-center text-muted-foreground">
                  Sin batches en curso. {ordenDia ? 'Genere los batches de la orden del día.' : 'No hay orden de producción para esta fecha.'}
                </td></tr>
              )}
            </tbody>
          </table>
          {despachados.length > 0 && (
            <p className="pt-2 text-xs text-muted-foreground">
              Despachados: {despachados.map((b) => `${b.receta} (${num(b.porciones_reales ?? b.porciones_plan, 0)})`).join(' · ')}
            </p>
          )}
        </CardContent>
      </Card>

      {/* Gantt de carga de equipos */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Carga de equipos del día</CardTitle>
          <p className="text-xs text-muted-foreground">
            Barras solapadas en el mismo equipo = conflicto de capacidad. Se ve antes de que ocurra.
          </p>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {ventana && carga.map((c) => (
            <div key={c.equipo} className="flex items-center gap-2">
              <div className="w-44 shrink-0 text-xs">
                {c.equipo} <span className="text-muted-foreground">×{c.unidades}</span>
                {c.solapes > 0 && <span className="ml-1 text-red-600 font-semibold">⚠ {c.solapes}</span>}
              </div>
              <div className="relative h-7 flex-1 rounded bg-muted/50">
                {c.barras.map((b) => {
                  const ini = new Date(b.inicio).getTime();
                  const fin = new Date(b.fin).getTime();
                  const left = ((ini - ventana.min) / ventana.span) * 100;
                  const width = Math.max(((fin - ini) / ventana.span) * 100, 2);
                  return (
                    <div key={b.id} title={`${b.receta} · ${hhmm(b.inicio)}–${hhmm(b.fin)}`}
                      className={`absolute top-0.5 h-6 rounded px-1 overflow-hidden text-[10px] leading-6 text-white ${
                        b.estado === 'despachado' ? 'bg-muted-foreground'
                        : b.estado === 'detenido' ? 'bg-purple-600'
                        : b.estado === 'planificado' ? 'bg-sky-600' : 'bg-emerald-600'}`}
                      style={{ left: `${left}%`, width: `${width}%` }}>
                      {b.receta}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
          {ventana && (
            <div className="flex justify-between pl-46 text-xs text-muted-foreground">
              <span>{hhmm(new Date(ventana.min).toISOString())}</span>
              <span>{hhmm(new Date(ventana.max).toISOString())}</span>
            </div>
          )}
          {!ventana && <p className="text-sm text-muted-foreground">Sin batches para graficar.</p>}
        </CardContent>
      </Card>

      {/* Panel de acción (temperatura obligatoria / porciones / detención) */}
      {accion && (
        <PanelIntegrado
          title={accion.tipo === 'detener' ? 'Detener batch' : `${PISO_ESTADO_LABEL[proximo(accion.batch) ?? '']} — ${accion.batch.receta}`}
          onClose={() => setAccion(null)}
        >
          <p className="-mt-1 pb-2 text-xs text-muted-foreground">{accion.batch.codigo} · {num(accion.batch.porciones_plan, 0)} porciones</p>
          <div className="space-y-3">
              {accion.tipo === 'avanzar' && proximo(accion.batch) === 'listo' && accion.batch.temp_min_c != null && (
                <div>
                  <p className="pb-1 text-sm font-medium">
                    Temperatura interna (°C) — mínimo {Number(accion.batch.temp_min_c).toFixed(0)} °C
                  </p>
                  <input className={`${fieldCls} text-2xl font-mono h-14`} inputMode="decimal" autoFocus
                    value={temp} onChange={(e) => setTemp(e.target.value)} placeholder="74" />
                  <p className="pt-1 text-xs text-muted-foreground">
                    Punto crítico de control: si no alcanza el mínimo, el batch no avanza y hay que continuar la cocción.
                  </p>
                </div>
              )}
              {accion.tipo === 'avanzar' && ['porcionado', 'despachado'].includes(proximo(accion.batch) ?? '') && (
                <div>
                  <p className="pb-1 text-sm font-medium">Porciones reales obtenidas</p>
                  <input className={`${fieldCls} text-2xl font-mono h-14`} inputMode="numeric"
                    value={porciones} onChange={(e) => setPorciones(e.target.value)} />
                  <p className="pt-1 text-xs text-muted-foreground">Plan: {num(accion.batch.porciones_plan, 0)} porciones (control de rendimiento).</p>
                </div>
              )}
              {accion.tipo === 'detener' && (
                <>
                  <div>
                    <p className="pb-1 text-sm font-medium">Motivo</p>
                    <div className="grid grid-cols-2 gap-2">
                      {MOTIVOS_DETENCION.map((m) => (
                        <button key={m} onClick={() => setMotivo(m)}
                          className={`rounded-md border-2 px-3 py-3 text-sm font-medium ${motivo === m ? 'border-primary bg-accent' : ''}`}>
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                  <input className={fieldCls} placeholder="Detalle (opcional)" value={detalle} onChange={(e) => setDetalle(e.target.value)} />
                </>
              )}
              <div>
                <p className="pb-1 text-sm font-medium">Responsable</p>
                <input className={fieldCls} value={responsable} onChange={(e) => setResponsable(e.target.value)} placeholder="Quién lo hizo" />
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" onClick={() => setAccion(null)}>Cancelar</Button>
                <Button disabled={mover.isPending} onClick={() => {
                  if (accion.tipo === 'detener') {
                    mover.mutate({ _batch_id: accion.batch.id, _estado: 'detenido',
                      _motivo: detalle ? `${motivo}: ${detalle}` : motivo, _responsable: responsable || null });
                  } else {
                    mover.mutate({ _batch_id: accion.batch.id, _estado: proximo(accion.batch),
                      _temperatura: temp ? parseFloat(temp) : null,
                      _porciones: porciones ? parseFloat(porciones) : null,
                      _responsable: responsable || null });
                  }
                }}>Confirmar</Button>
              </div>
          </div>
        </PanelIntegrado>
      )}
    </div>
  );
}
